import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AuthLayout from "../../components/layouts/auth";
import NairaSvg from "../../components/naira-svg";
import ApiRequest from "../../libs/ApiCall";
import moment from "moment";
import { ErrorFlash, SuccessFlash } from "../../libs/flash-notification";
import { numberWithCommas, SmoothScroll } from "../../libs/helper-support";
import Paystack from "@paystack/inline-js";
import PaginationType from "../../types/pagination-type";

const Payment = () => {

    interface PaymentType{
        reference: string
        name:string,
        description:string,
        amount:string,
        paymentType:string,
        startAt:string,
        uuid:string,
    }

    const [searchParams, setSearchParams] = useSearchParams();

    const reference = searchParams.get('reference');

    const [payments, setPayments] = useState<PaymentType[]>([]);

    const [duePayments, setDuePayments] = useState<PaymentType[]>([]);

    const [onLoadPayments, setOnLoadPayments] = useState<string | null>(null);

    const [paymentData, setPaymentData] = useState<PaginationType>({} as PaginationType);

    const [paymentPage, setPaymentPage] = useState<number>(1);

    const getPayments = async () => {
        const request = await ApiRequest({endpoint: `/payment?page=${paymentPage}`, method: 'GET'});
        if ( request.status !== '200' ) return
        setPayments(request.data.items);
        setPaymentData(request.data);
        SmoothScroll();
    }

    const getDuePayments = async () => {
        const request = await ApiRequest({endpoint: '/payment/due-payment', method: 'GET'});
        request.status === '200' && setDuePayments(request.data);
    }

    const verifyTransactionSuccess = async () => {
        if ( ! reference ) return;
        
        const request = await ApiRequest({endpoint: `payment/verify/${reference}`, method: 'GET'});

        if ( request.status === '302' ) return;

        if ( request.status !== '200' ) { 
            ErrorFlash('Transaction processing.');
            return;
        }
        
        if(request.data.status === 'success'){
            SuccessFlash('Payment successful!');
            getDuePayments()
            getPayments();
        }
    }

    useEffect(()=> {
        verifyTransactionSuccess()
        getDuePayments()
    }, []);

   // window.location.replace(window.location.href);

    useEffect(() => {getPayments()}, [paymentPage]);

  
    const initiatePayment = async (paymentId:string) => {
        setOnLoadPayments(paymentId);
        const request = await ApiRequest({endpoint: `/payment/initiate-repayment`, method: 'POST', payload: {paymentId}});
        setOnLoadPayments(null);
        if ( request.status === '200') {
            //popup.resumeTransaction(request.data.accessCode);

            const currentLocation : string = window.location.href;

            window.location.replace(currentLocation);
        
            window.location.replace(request.data.url);
            return; 
        }
        ErrorFlash('Could not initiate payment, try again.');
    }

    return (
        <AuthLayout>
            <div className="page-outer">
                <h1 className="page-title">Payments</h1>
                <h4 className="mt-10 page-title text-[18px]">Due payments</h4>
                {duePayments.length === 0 && <p className="mt-2">No due payments</p>}
                <div className="grid gap-4 mt-8 lg:grid-cols-3 lg:w-4/5">
                    {duePayments.map((payment, i) => (
                        <div key={i} className="p-5 border rounded-md border-[#E7E7E7]">
                        <h3 className="text-[16px]">{payment.name}</h3>
                        <p className="text-light-black text-[14px] mt-2">{payment.description}</p>
                        <div className="my-5">
                            <NairaSvg/>{numberWithCommas(payment.amount)}
                        </div>
                        <button disabled={!(onLoadPayments ?? true)} onClick={() => initiatePayment(payment.uuid)} type="button" className={`${onLoadPayments && 'opacity-[0.5] animate-pulse'} pryBtn w-fit`}>
                            {onLoadPayments  === payment.uuid ? 'Processing...' : 'Pay Now'}
                        </button>
                    </div>
                    ))}
                </div>
                <h4 className="mt-10 page-title text-[18px]">Payment History</h4>
                <div className='relative mt-5 overflow-x-auto'>
                    <table className='text-tertiary text-[14px] w-full relative z-10'>
                        <thead>
                            <tr className='bg-[#F8F8F8]'>
                                <td className="py-3 pl-4">Date</td>
                                <td className="px-6 py-3">Amount</td>
                                <td className="px-6 py-3">Payment Type</td>
                                <td className="px-6 py-3">Description</td>
                            </tr>
                        </thead>
                        <tbody className="relative">
                        {payments.map((payment, i) => (
                            <tr key={i} className='border-b border-[#E6EAF0]'>
                                <td className="px-3 py-3">{moment(payment.startAt).format('MMM DD YYYY')}</td>
                                <td className="px-6 py-3">
                                    <NairaSvg/>{numberWithCommas(payment.amount)}
                                </td>
                                <td className="px-6 py-3">{payment.name}</td>
                                <td className="px-6 py-3">{payment.description}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
                <div className="flex justify-end mt-5 space-x-3 w-fit">
                        {paymentPage > 1 && <button onClick={() => setPaymentPage(paymentPage - 1)} type="button" className="font-[600] text-[14px] pryBtnOutline border-[#E7E7E7] text-black hover:bg-transparent hover:text-black">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M15.5305 18.9693C15.6002 19.039 15.6554 19.1217 15.6932 19.2128C15.7309 19.3038 15.7503 19.4014 15.7503 19.4999C15.7503 19.5985 15.7309 19.6961 15.6932 19.7871C15.6554 19.8781 15.6002 19.9609 15.5305 20.0306C15.4608 20.1002 15.3781 20.1555 15.287 20.1932C15.196 20.2309 15.0984 20.2503 14.9999 20.2503C14.9013 20.2503 14.8037 20.2309 14.7127 20.1932C14.6216 20.1555 14.5389 20.1002 14.4692 20.0306L6.96923 12.5306C6.8995 12.4609 6.84418 12.3782 6.80644 12.2871C6.76869 12.1961 6.74927 12.0985 6.74927 11.9999C6.74927 11.9014 6.76869 11.8038 6.80644 11.7127C6.84418 11.6217 6.8995 11.539 6.96923 11.4693L14.4692 3.9693C14.61 3.82857 14.8008 3.74951 14.9999 3.74951C15.1989 3.74951 15.3898 3.82857 15.5305 3.9693C15.6712 4.11003 15.7503 4.30091 15.7503 4.49993C15.7503 4.69895 15.6712 4.88982 15.5305 5.03055L8.56017 11.9999L15.5305 18.9693Z" fill="#888888"/>
                            </svg>
                            <span>Previous</span>
                        </button>}
                        {paymentData.hasMorePages &&  <button onClick={() => setPaymentPage(paymentPage + 1)} type="button" className="font-[600] text-[14px] pryBtnOutline border-[#E7E7E7] text-black hover:bg-transparent hover:text-black">
                            <span>Next</span>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17.0306 12.5306L9.53055 20.0306C9.46087 20.1002 9.37815 20.1555 9.2871 20.1932C9.19606 20.2309 9.09847 20.2503 8.99993 20.2503C8.90138 20.2503 8.8038 20.2309 8.71276 20.1932C8.62171 20.1555 8.53899 20.1002 8.4693 20.0306C8.39962 19.9609 8.34435 19.8781 8.30663 19.7871C8.26892 19.6961 8.24951 19.5985 8.24951 19.4999C8.24951 19.4014 8.26892 19.3038 8.30663 19.2128C8.34435 19.1217 8.39962 19.039 8.4693 18.9693L15.4396 11.9999L8.4693 5.03055C8.32857 4.88982 8.24951 4.69895 8.24951 4.49993C8.24951 4.30091 8.32857 4.11003 8.4693 3.9693C8.61003 3.82857 8.80091 3.74951 8.99993 3.74951C9.19895 3.74951 9.38982 3.82857 9.53055 3.9693L17.0306 11.4693C17.1003 11.539 17.1556 11.6217 17.1933 11.7127C17.2311 11.8038 17.2505 11.9014 17.2505 11.9999C17.2505 12.0985 17.2311 12.1961 17.1933 12.2871C17.1556 12.3782 17.1003 12.4609 17.0306 12.5306Z" fill="#888888"/>
                            </svg>
                        </button>}
                    </div>
            </div>
        </AuthLayout>
    );
}

export default Payment;